import React, { useState, useEffect } from 'react';
import { 
  PlusCircle, 
  Edit2, 
  Trash2, 
  X, 
  Image as ImageIcon,
  Calendar,
  User,
  Upload,
  MessageSquare,
  Users,
  Video
} from 'lucide-react';
import englishService from '../services/englishService';
import RichTextEditor from '../components/RichTextEditor';

// Custom Alert Component
const Alert = ({ children, variant, className = '' }) => {
  const baseStyles = 'flex w-full rounded-lg border p-4';
  const variantStyles = variant === 'destructive' 
    ? 'border-red-200 bg-red-50 text-red-600'
    : 'border-gray-200 bg-gray-50 text-gray-600';

  return (
    <div className={`${baseStyles} ${variantStyles} ${className}`}>
      {children}
    </div>
  );
};

const AlertDescription = ({ children, className = '' }) => (
  <div className={`text-sm ${className}`}>
    {children}
  </div>
);

const InterviewManagement = () => {
  const [interviews, setInterviews] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    thumbnail: '',
    content: [''],
    date: new Date().toISOString().split('T')[0],
    interviewer: '',
    interviewee: '',
    videoLink: '', 
    images: []
  });

  useEffect(() => {
    fetchInterviews();
  }, []);

  const fetchInterviews = async () => {
    try {
      setLoading(true);
      const response = await englishService.interviews.getAll();
      
      // Check if response exists and has the expected structure
      if (response && response.data) {
        // Handle both possible API response structures
        const interviewsData = Array.isArray(response.data) 
          ? response.data 
          : (response.data.data || []);
        
        setInterviews(interviewsData);
        setError(''); // Clear any existing errors
      } else {
        console.error('Unexpected response structure:', response);
        setError('Invalid response format from server');
        setInterviews([]);
      }
    } catch (error) {
      console.error('Error fetching interviews:', error);
      setError(error.message || 'Failed to fetch interviews');
      setInterviews([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
    if (name === 'content') {
      const newContent = [...formData.content];
      newContent[index] = value;
      setFormData(prev => ({ ...prev, content: newContent }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'thumbnail') {
          setFormData(prev => ({ ...prev, thumbnail: reader.result }));
        } else if (type === 'image') {
          if (formData.images.length >= 5) {
            setError('Maximum 5 images are allowed');
            return;
          }
          setFormData(prev => ({
            ...prev,
            images: [...prev.images, reader.result]
          }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    // Clear previous errors
    setError('');
    
    if (!formData.title || !formData.title.trim()) {
      setError('Title is required');
      return false;
    }
    
    if (!formData.thumbnail || !formData.thumbnail.trim()) {
      setError('Thumbnail is required');
      return false;
    }
    
    // Filter out empty content strings and validate
    const validContent = formData.content.filter(content => content && content.trim() !== '');
    if (validContent.length === 0) {
      setError('At least one paragraph of content is required');
      return false;
    }
    
    if (!formData.interviewee || !formData.interviewee.trim()) {
      setError('Interviewee is required');
      return false;
    }
    
    if (formData.images.length > 5) {
      setError('Maximum 5 images are allowed');
      return false;
    }
    
    return true;
  };

  const prepareFormData = () => {
    // Filter out empty content strings before submission
    const cleanedContent = formData.content.filter(content => content && content.trim() !== '');
    
    return {
      ...formData,
      content: cleanedContent,
      title: formData.title.trim(),
      interviewee: formData.interviewee.trim(),
      interviewer: formData.interviewer.trim(),
      videoLink: formData.videoLink.trim()
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError('');
      
      const cleanedFormData = prepareFormData();
      
      if (selectedInterview) {
        await englishService.interviews.update(selectedInterview._id, cleanedFormData);
        setSuccess('Interview updated successfully!');
      } else {
        await englishService.interviews.create(cleanedFormData);
        setSuccess('Interview created successfully!');
      }
      
      await fetchInterviews();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Form submission error:', error);
      setError(error.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (interview) => {
    setSelectedInterview(interview);
    setFormData({
      title: interview.title || '',
      content: interview.content && interview.content.length > 0 ? interview.content : [''],
      thumbnail: interview.thumbnail || '',
      date: interview.date ? new Date(interview.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      interviewer: interview.interviewer || '',
      interviewee: interview.interviewee || '',
      videoLink: interview.videoLink || '',
      images: interview.images || []
    });
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await englishService.interviews.delete(selectedInterview._id);
      setSuccess('Interview deleted successfully!');
      await fetchInterviews();
      setShowDeleteModal(false);
      setSelectedInterview(null);
    } catch (error) {
      setError(error.message || 'Failed to delete interview');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      thumbnail: '',
      content: [''],
      date: new Date().toISOString().split('T')[0],
      interviewer: '',
      interviewee: '',
      videoLink: '',
      images: []
    });
    setSelectedInterview(null);
    setError('');
    setSuccess('');
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // Clear success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Clear error message after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <div className="min-h-screen bg-white p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Interview Management</h1>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors"
        >
          <PlusCircle className="mr-2" size={20} />
          Add Interview
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {success && (
        <Alert className="mb-4 bg-green-50 text-green-700 border-green-200">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* Loading and Interviews List */}
      {loading && !showModal && !showDeleteModal ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
        </div>
      ) : interviews.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600">No interviews found. Create one to get started!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {interviews.map((interview) => (
            <div key={interview._id} className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
              <div className="h-48 overflow-hidden">
                <img
                  src={interview.thumbnail || '/api/placeholder/400/200'}
                  alt={interview.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">{interview.title}</h3>
                <div className="space-y-2 text-gray-600 text-sm mb-4">
                  <div className="flex items-center">
                    <Calendar size={16} className="mr-2" />
                    {new Date(interview.date).toLocaleDateString()}
                  </div>
                  <div className="flex items-center">
                    <Users size={16} className="mr-2" />
                    <span className="font-medium">Interviewee:</span>
                    <span className="ml-1">{interview.interviewee}</span>
                  </div>
                  {interview.interviewer && (
                    <div className="flex items-center">
                      <User size={16} className="mr-2" />
                      <span className="font-medium">Interviewer:</span>
                      <span className="ml-1">{interview.interviewer}</span>
                    </div>
                  )}
                  <div className="flex items-center">
                    <MessageSquare size={16} className="mr-2" />
                    {interview.comments?.length || 0} comments
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  {interview.videoLink && (
                    <a
                      href={interview.videoLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-sky-500 hover:text-sky-600"
                    >
                      <Video size={16} className="mr-1" />
                      Watch Video
                    </a>
                  )}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(interview)}
                      className="p-2 text-sky-500 hover:bg-sky-50 rounded-full transition-colors"
                    >
                      <Edit2 size={20} />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedInterview(interview);
                        setShowDeleteModal(true);
                      }}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  {selectedInterview ? 'Edit Interview' : 'Add Interview'}
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    placeholder="Enter interview title"
                  />
                </div>

                {/* Interviewee */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Interviewee *
                  </label>
                  <input
                    type="text"
                    name="interviewee"
                    value={formData.interviewee}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    placeholder="Enter interviewee name"
                  />
                </div>

                {/* Interviewer */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Interviewer
                  </label>
                  <input
                    type="text"
                    name="interviewer"
                    value={formData.interviewer}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    placeholder="Enter interviewer name"
                  />
                </div>

                {/* Video Link */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Video Link
                  </label>
                  <input
                    type="text"
                    name="videoLink"
                    value={formData.videoLink}
                    onChange={handleInputChange}
                    placeholder="Enter video URL"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  />
                </div>

                {/* Thumbnail */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Thumbnail *
                  </label>
                  <div className="flex items-center space-x-4">
                    {formData.thumbnail && (
                      <img
                        src={formData.thumbnail}
                        alt="Thumbnail preview"
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                    )}
                    <div className="flex-1">
                      <label className="flex flex-col items-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-sky-500">
                        <ImageIcon className="text-gray-400 mb-2" size={24} />
                        <span className="text-sm text-gray-600">Upload thumbnail</span>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => handleFileChange(e, 'thumbnail')}
                        />
                      </label>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content *
                  </label>
                  <RichTextEditor
                    value={formData.content}
                    onChange={(content) => setFormData(prev => ({ ...prev, content }))}
                    onError={setError}
                  />
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date *
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  />
                </div>

                {/* Additional Images */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Images (Max 5)
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {formData.images.map((image, index) => (
                      <div key={index} className="relative">
                        <img
                          src={image}
                          alt={`Additional ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                    {formData.images.length < 5 && (
                      <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-sky-500">
                        <Upload className="text-gray-400 mb-2" size={24} />
                        <span className="text-sm text-gray-600">Add image</span>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => handleFileChange(e, 'image')}
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {selectedInterview ? 'Updating...' : 'Creating...'}
                      </div>
                    ) : (
                      selectedInterview ? 'Update Interview' : 'Create Interview'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedInterview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Delete Interview</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{selectedInterview.title}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedInterview(null);
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Deleting...
                  </div>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewManagement;
